/** The view is presentation state; search criteria use existing dashboard keys. */
export type FsPlayId = 'fx_trade' | 'lending' | 'working_capital' | 'ownership' | 'cash_treasury';

export interface FsPlay {
  id: FsPlayId;
  label: string;
  shortLabel: string;
  description: string;
  icon: string;
}

/** The same views as the Financial Services profile, in the same order (FX first). */
export const FS_PLAYS: FsPlay[] = [
  {
    id: 'fx_trade',
    label: 'FX, trade and payments',
    shortLabel: 'FX and trade',
    description: 'Find importers, exporters and companies with currency exposure.',
    icon: 'fa-money-bill-transfer',
  },
  {
    id: 'lending',
    label: 'Lending and refinancing',
    shortLabel: 'Lending',
    description: 'Find incumbents, secured lending and likely borrowing needs.',
    icon: 'fa-building-columns',
  },
  {
    id: 'working_capital',
    label: 'Working capital and assets',
    shortLabel: 'Working capital',
    description: 'Find debtor-heavy and asset-heavy companies for invoice and asset finance.',
    icon: 'fa-boxes-stacked',
  },
  {
    id: 'ownership',
    label: 'Ownership, M&A and succession',
    shortLabel: 'Ownership',
    description: 'Find owner-managed companies, likely sellers and acquirers.',
    icon: 'fa-sitemap',
  },
  {
    id: 'cash_treasury',
    label: 'Cash and treasury',
    shortLabel: 'Cash',
    description: 'Find cash-rich companies and maturing finance functions.',
    icon: 'fa-vault',
  },
];

/** Existing financial slider definitions are supplied by dashboard initialisation. */
export interface FsFinancialSlider {
  key: string;
  label: string;
}

export const FS_COMMON_FINANCIAL_SLIDERS: FsFinancialSlider[] = [
  { key: 'Revenue Growth Rate', label: 'Revenue growth (%)' },
];

/** EPC totals are licensed with the land title data, not energy/emissions access. */
export const FS_EPC_SLIDERS: FsFinancialSlider[] = [
  { key: 'EpcBuildingCountSelected', label: 'EPC building count' },
  { key: 'EpcTotalFloorAreaSelected', label: 'EPC total floor area (m²)' },
  { key: 'EpcMinAssetRatingSelected', label: 'EPC minimum asset rating' },
  { key: 'EpcTotalStandardEmissionsSelected', label: 'EPC standard emissions (tCO₂e)' },
  { key: 'EpcTotalTargetEmissionsSelected', label: 'EPC target emissions (tCO₂e)' },
  { key: 'EpcTotalBuildingEmissionsSelected', label: 'EPC building emissions (tCO₂e)' },
  { key: 'EpcBuildingsWithAirconCountSelected', label: 'EPC buildings with air conditioning' },
];

/** Tangible and intangible asset values and additions, shown in Customers, working capital and assets. */
export const FS_ASSET_SLIDERS: FsFinancialSlider[] = [
  { key: 'PlantAndMachineryTangibleAssets', label: 'Plant and machinery assets (£)' },
  { key: 'PlantAndMachineryCostTangibleAssets', label: 'Plant and machinery cost (£)' },
  { key: 'PlantAndMachineryAdditionsTangibleAssets', label: 'Plant and machinery additions (£)' },
  { key: 'VehiclesTangibleAssets', label: 'Vehicle assets (£)' },
  { key: 'ComputerEquipmentTangibleAssets', label: 'Computer equipment assets (£)' },
  { key: 'ComputerEquipmentAdditionsTangibleAssets', label: 'Computer equipment additions (£)' },
  { key: 'LandAndBuildingsTangibleAssets', label: 'Land and buildings assets (£)' },
  { key: 'LandAndBuildingsCostTangibleAssets', label: 'Land and buildings cost (£)' },
  { key: 'LandAndBuildingsAdditionsTangibleAssets', label: 'Land and buildings additions (£)' },
  { key: 'FurnitureAndFittingsTangibleAssets', label: 'Furniture and fittings assets (£)' },
  { key: 'FurnitureAndFittingsAdditionsTangibleAssets', label: 'Furniture and fittings additions (£)' },
  { key: 'LeaseholdAssetsTangibleAssets', label: 'Leasehold assets (£)' },
  { key: 'LeaseholdAssetsAdditionsTangibleAssets', label: 'Leasehold additions (£)' },
  { key: 'AssetsUnderConstructionTangibleAssets', label: 'Assets under construction (£)' },
  { key: 'AssetsUnderConstructionAdditionsTangibleAssets', label: 'Construction additions (£)' },
  { key: 'ComputerSoftwareIntangibleAssets', label: 'Computer software assets (£)' },
  { key: 'ComputerSoftwareAdditionsIntangibleAssets', label: 'Computer software additions (£)' },
  { key: 'PatentsTrademarksAndLicencesIntangibleAssets', label: 'Patent and licence assets (£)' },
  { key: 'PatentsTrademarksAndLicencesAdditionsIntangibleAssets', label: 'Patent and licence additions (£)' },
];

/**
 * Every filed profit and loss and balance sheet measure, offered as one searchable list in Scale and
 * financials. Grouped by view only so a shared search can be matched to the view it came from.
 */
export const FS_PLAY_FINANCIAL_SLIDERS: Record<FsPlayId, FsFinancialSlider[]> = {
  fx_trade: [
    { key: 'Fx Gain Or Loss', label: 'FX gain or loss (£)' },
    { key: 'Gross Margin Percentage', label: 'Gross margin (%)' },
  ],
  lending: [
    { key: 'Ebitda', label: 'EBITDA (£)' },
    { key: 'Operating Profit', label: 'Operating profit (£)' },
    { key: 'Profit Before Tax', label: 'Profit before tax (£)' },
    { key: 'Profit After Tax', label: 'Profit after tax (£)' },
    { key: 'Interest Payable', label: 'Interest payable (£)' },
    { key: 'Gross Profit', label: 'Gross profit (£)' },
    { key: 'Gross Profit Growth Rate', label: 'Gross profit growth (%)' },
    { key: 'Cost Of Sales', label: 'Cost of sales (£)' },
    { key: 'Operating Expenses', label: 'Operating expenses (£)' },
    { key: 'Corporation Tax', label: 'Corporation tax (£)' },
    { key: 'Tax Credit', label: 'Tax credit in accounts (£)' },
    { key: 'Revenue Absolute Growth', label: 'Revenue absolute growth (£)' },
    { key: 'Total Assets', label: 'Total assets (£)' },
    { key: 'Total Assets Growth Rate', label: 'Asset growth (%)' },
    { key: 'Fixed Assets', label: 'Fixed assets (£)' },
    { key: 'Fixed Assets Growth Rate', label: 'Fixed asset growth (%)' },
    { key: 'Fixed Assets Absolute Growth', label: 'Fixed asset absolute growth (£)' },
    { key: 'Total Assets Minus Current Liabilities', label: 'Total assets less current liabilities (£)' },
    { key: 'Creditors Due After One Year', label: 'Creditors due after one year (£)' },
    { key: 'Total Creditors', label: 'Total creditors (£)' },
    { key: 'Shareholders Funds', label: 'Shareholders’ funds (£)' },
    { key: 'Capital And Reserves', label: 'Capital and reserves (£)' },
    { key: 'Retained Earnings', label: 'Retained earnings (£)' },
    { key: 'Share Premium', label: 'Share premium (£)' },
  ],
  working_capital: [
    { key: 'Current Assets', label: 'Current assets (£)' },
    { key: 'Current Assets Growth Rate', label: 'Current assets growth (%)' },
    { key: 'Working Capital', label: 'Working capital (£)' },
    { key: 'Trade Debtors', label: 'Trade debtors (£)' },
    { key: 'Debtors Due Within One Year', label: 'Debtors due within one year (£)' },
    { key: 'Debtors Due After One Year', label: 'Debtors due after one year (£)' },
    { key: 'Other Debtors', label: 'Other debtors (£)' },
    { key: 'Total Debtors', label: 'Total debtors (£)' },
    { key: 'Creditors Due Within One Year', label: 'Creditors due within one year (£)' },
    { key: 'Inventory', label: 'Inventory (£)' },
    { key: 'Raw Materials And Consumable', label: 'Raw materials and consumables (£)' },
    { key: 'Raw Materials And Consumable Growth Rate', label: 'Raw materials growth (%)' },
    { key: 'Wages And Salaries', label: 'Wages and salaries (£)' },
    { key: 'Wages And Salaries Growth Rate', label: 'Wages growth (%)' },
    { key: 'Wages And Salaries Absolute Growth', label: 'Wages absolute growth (£)' },
  ],
  ownership: [
    { key: 'Dividends', label: 'Dividends (£)' },
  ],
  cash_treasury: [
    { key: 'Cash At Bank', label: 'Cash at bank (£)' },
    { key: 'Cash At Bank Growth Rate', label: 'Cash at bank growth (%)' },
  ],
};

const rangeKeys = (sliders: FsFinancialSlider[]): string[] =>
  sliders.flatMap(({ key }) => [`lower${key}`, `upper${key}`]);

const financialLabels = (sliders: FsFinancialSlider[]): Record<string, string> =>
  Object.fromEntries(sliders.flatMap(({ key, label }) => [
    [`lower${key}`, `${label} from`], [`upper${key}`, `${label} to`],
  ]));

/** Foundations plus the groups that are not tied to one view. */
export const FS_COMMON_FILTER_KEYS = [
  'companyNameContains',
  'selectedCompanyStatuses',
  'selectedTerritory',
  'ukRegion',
  'postcodeBeginsWith',
  'selectedSICCodes',
  'excludedSICCodes',
  'selectedAddressTypeOption',
  'postcodeDistrict',
  'postcodeWithinRadius',
  'mileRadiusOfPostcode',
  'lowerAge',
  'upperAge',
  'employeeLowerCount',
  'employeeUpperCount',
  'lowerHeadCountGrowthPercentage',
  'upperHeadCountGrowthPercentage',
  'excludeEstimatedHeadcountFigures',
  'lowerRevenue',
  'upperRevenue',
  'ignoreRevenueEstimates',
  ...rangeKeys(FS_COMMON_FINANCIAL_SLIDERS),
  ...rangeKeys(Object.values(FS_PLAY_FINANCIAL_SLIDERS).flat()),
  'corporateStructureIsTopLevelParentCompany',
  'corporateStructureHasParent',
  'corporateStructureDoesNotHaveParent',
  'corporateStructureHasChild',
  'hasOverseasParent',
  'hasOfficesOverseas',
  'hasWebsite',
  'hasPhoneNumber',
  'hasEmailAddress',
  'accountsSearchTerm',
  'accountsSearchWithinDays',
  'newsContaining',
  'newsWithinDays',
  'newsSource',
  'homepageSearchTerm',
  'fullWebsiteSearchTerm',
  'summarySemanticSearchTerm',
  'hiringJobTitleContains',
  'hiringDescriptionContains',
  'selectedHiringDayLimit',
  'recentDirectorshipTitleContains',
  'lowerRecentDirectorshipMonth',
  'upperRecentDirectorshipMonth',
  'directorJobTitleContains',
  'selectedCustomColumns',
  'tagPositiveIdsSelected',
  'tagNegativeIdsSelected',
  'excludeCompaniesTaggedByTeam',
  'lowerHeatScore',
  'upperHeatScore',
  // Credit appetite and accounts filing
  'selectedLatestAccountsAuditOption',
  'accountsAreOverdue',
  'lowerAccountsDueSelected', 'upperAccountsDueSelected',
  'lowerLatestAccountsSubmittedSelected', 'upperLatestAccountsSubmittedSelected',
  'selectedLatestAccountsGroupOption',
  'selectedFinancialYearEnds',
  // Trigger events and timing
  'isHiring', 'includeRecruiters',
  'lowerNumberOfRolesHiringFor', 'upperNumberOfRolesHiringFor', 'selectedNumberOfRolesHiringForDayLimit',
  'lowerDaysUntilFinancialYearEnd', 'upperDaysUntilFinancialYearEnd',
  'lowerDaysUntilAnniversary', 'upperDaysUntilAnniversary',
  'trademarkNameContains', 'trademarkDescriptionContains',
  // Sustainability and green finance
  'lowerTotalEnergyKwhSelected', 'upperTotalEnergyKwhSelected',
  'lowerGasKwhSelected', 'upperGasKwhSelected',
  'lowerElectricKwhSelected', 'upperElectricKwhSelected',
  'isLowEnergy',
  'lowerScope1KwhSelected', 'upperScope1KwhSelected',
  'lowerScope2KwhSelected', 'upperScope2KwhSelected',
  'lowerScope3KwhSelected', 'upperScope3KwhSelected',
  'lowerTotalEmissionsKgCo2eSelected', 'upperTotalEmissionsKgCo2eSelected',
  'lowerScope1KgCo2eSelected', 'upperScope1KgCo2eSelected',
  'lowerScope2KgCo2eSelected', 'upperScope2KgCo2eSelected',
  'lowerScope3KgCo2eSelected', 'upperScope3KgCo2eSelected',
  'lowerKgCo2ePerMillionRevenueSelected', 'upperKgCo2ePerMillionRevenueSelected',
  'lowerKgCo2ePerEmployeeSelected', 'upperKgCo2ePerEmployeeSelected',
  'hasLandTitle', 'landTenure', 'landCategory',
  ...rangeKeys(FS_EPC_SLIDERS),
  // Advisers, relationships and digital footprint
  'accountantNameContains', 'selectedAccountants',
  'auditorNameContains', 'selectedAuditors',
  'lawyerNameContains',
  'supplierTagSelected', 'customerTagSelected', 'associateWithOneSupplierOnly',
  'webTrafficLow', 'webTrafficHigh',
] as const;

export const FS_PLAY_FILTER_KEYS: Record<FsPlayId, readonly string[]> = {
  fx_trade: [
    'selectedOfficeCountries',
    'lowerGeographicalRevenueSelected', 'upperGeographicalRevenueSelected',
    'selectedGeographicalRevenueTerritories',
    'selectedGeographicalRevenueCountries',
    'selectedImportExportCategories',
    'selectedImportingOrExportingOption',
    'importExportTimeScale',
    'importExportFrequencyImportingOrExporting', 'importExportFrequency', 'importExportFrequencyMonthCount',
    'lowerNumberOfKnownOfficeLocations', 'upperNumberOfKnownOfficeLocations',
    'hasLEINumber', 'selectedLEIStatus',
  ],
  lending: [
    'bankerNameContains',
    'selectedBankers',
    'noOutstandingCharges',
    'chargeRegistered',
    'outstandingChargeRegistered',
    'chargeHolderNameContains',
    'chargeCreatedMonthsAgoLower', 'chargeCreatedMonthsAgoUpper',
    'ignoreSatisfiedCharges',
  ],
  working_capital: [
    ...rangeKeys(FS_ASSET_SLIDERS),
    'amazonSeller', 'listedOnGCloud', 'hasContract',
    'lowerContractValueSelected', 'upperContractValueSelected', 'contractAwardedWithinMonths',
    'lowerContractStartMonthsSelected', 'upperContractStartMonthsSelected',
    'lowerContractEndMonthsSelected', 'upperContractEndMonthsSelected',
    'contractTitleContains', 'contractDescriptionContains',
  ],
  ownership: [
    'lowerNumberOfUBOs', 'upperNumberOfUBOs', 'selectedUBOCountries',
    'lowerNumberOfPSCs', 'upperNumberOfPSCs', 'PSCsRealPersonsOnly', 'nationalitiesOfPSCsSelected',
    'lowerNumberOfDirectors', 'upperNumberOfDirectors', 'nationalitiesOfDirectorsSelected',
    'lowerAverageDirectorAgeRange', 'upperAverageDirectorAgeRange',
    'hasShareFilingEvent', 'shareFilingEventWithinDays', 'lowerShareFilingCapitalValue', 'upperShareFilingCapitalValue',
    'hasRaisedInvestment', 'lowerInvestmentAmount', 'upperInvestmentAmount',
    'issuedAGrant', 'lowerGrantAmount', 'upperGrantAmount',
    'grantLowerMonthsUntilProjectEnd', 'grantUpperMonthsUntilProjectEnd',
    'hasAnyPatentRecord', 'hasActivePatent', 'patentFiledWithinDays',
    'lowerPatentAwardedDays', 'upperPatentAwardedDays',
    'similarCompanyUrl',
  ],
  cash_treasury: [],
};

export const FS_FILTER_LABELS: Record<string, string> = {
  companyNameContains: 'Company name',
  selectedCompanyStatuses: 'Company status',
  selectedTerritory: 'Territory',
  ukRegion: 'UK region',
  postcodeBeginsWith: 'Postcode starts with',
  selectedSICCodes: 'Industry',
  excludedSICCodes: 'Excluded industry',
  selectedAddressTypeOption: 'Address type',
  postcodeDistrict: 'Postcode district',
  postcodeWithinRadius: 'Postcode radius centre',
  mileRadiusOfPostcode: 'Radius (miles)',
  lowerAge: 'Company age from',
  upperAge: 'Company age to',
  employeeLowerCount: 'Employees from',
  employeeUpperCount: 'Employees to',
  lowerHeadCountGrowthPercentage: 'Headcount growth from',
  upperHeadCountGrowthPercentage: 'Headcount growth to',
  excludeEstimatedHeadcountFigures: 'Filed headcount only',
  lowerRevenue: 'Revenue from',
  upperRevenue: 'Revenue to',
  ignoreRevenueEstimates: 'Filed revenue only',
  ...financialLabels(FS_COMMON_FINANCIAL_SLIDERS),
  ...financialLabels(Object.values(FS_PLAY_FINANCIAL_SLIDERS).flat()),
  ...financialLabels(FS_ASSET_SLIDERS),
  ...financialLabels(FS_EPC_SLIDERS),
  corporateStructureIsTopLevelParentCompany: 'Top-level parent',
  corporateStructureHasParent: 'Has parent',
  corporateStructureDoesNotHaveParent: 'Standalone / no parent',
  corporateStructureHasChild: 'Has subsidiaries',
  hasOverseasParent: 'Overseas parent',
  hasOfficesOverseas: 'Overseas office',
  hasWebsite: 'Has website',
  hasPhoneNumber: 'Has phone number',
  hasEmailAddress: 'Has email address',
  accountsSearchTerm: 'Accounts mention',
  accountsSearchWithinDays: 'Accounts filed within days',
  newsContaining: 'News mentions',
  newsWithinDays: 'News within days',
  newsSource: 'News source',
  homepageSearchTerm: 'Homepage mentions',
  fullWebsiteSearchTerm: 'Website mentions',
  summarySemanticSearchTerm: 'Product or service',
  hiringJobTitleContains: 'Hiring role',
  hiringDescriptionContains: 'Hiring description',
  selectedHiringDayLimit: 'Hiring posted within days',
  recentDirectorshipTitleContains: 'Recent director role',
  lowerRecentDirectorshipMonth: 'Appointment age from',
  upperRecentDirectorshipMonth: 'Appointment age to',
  directorJobTitleContains: 'Current leadership role',
  selectedCustomColumns: 'Workspace or propensity field',
  tagPositiveIdsSelected: 'Tagged with',
  tagNegativeIdsSelected: 'Not tagged with',
  excludeCompaniesTaggedByTeam: 'Exclude team-tagged companies',
  lowerHeatScore: 'Heat Score from',
  upperHeatScore: 'Heat Score to',
  selectedLatestAccountsAuditOption: 'Latest audit status',
  accountsAreOverdue: 'Accounts overdue',
  lowerAccountsDueSelected: 'Accounts due from days',
  upperAccountsDueSelected: 'Accounts due to days',
  lowerLatestAccountsSubmittedSelected: 'Latest accounts filed from days',
  upperLatestAccountsSubmittedSelected: 'Latest accounts filed to days',
  selectedLatestAccountsGroupOption: 'Accounts type',
  selectedFinancialYearEnds: 'Year-end month',
  isHiring: 'Is hiring',
  includeRecruiters: 'Include recruiter listings',
  lowerNumberOfRolesHiringFor: 'Open roles from',
  upperNumberOfRolesHiringFor: 'Open roles to',
  selectedNumberOfRolesHiringForDayLimit: 'Roles posted within days',
  lowerDaysUntilFinancialYearEnd: 'Next year end from days',
  upperDaysUntilFinancialYearEnd: 'Next year end within days',
  lowerDaysUntilAnniversary: 'Anniversary from days',
  upperDaysUntilAnniversary: 'Anniversary within days',
  trademarkNameContains: 'Trademark name',
  trademarkDescriptionContains: 'Trademark description',
  lowerTotalEnergyKwhSelected: 'Energy use from',
  upperTotalEnergyKwhSelected: 'Energy use to',
  lowerGasKwhSelected: 'Gas use from',
  upperGasKwhSelected: 'Gas use to',
  lowerElectricKwhSelected: 'Electricity use from',
  upperElectricKwhSelected: 'Electricity use to',
  isLowEnergy: 'Reported low energy use',
  lowerScope1KwhSelected: 'Scope 1 energy from',
  upperScope1KwhSelected: 'Scope 1 energy to',
  lowerScope2KwhSelected: 'Scope 2 energy from',
  upperScope2KwhSelected: 'Scope 2 energy to',
  lowerScope3KwhSelected: 'Scope 3 energy from',
  upperScope3KwhSelected: 'Scope 3 energy to',
  lowerTotalEmissionsKgCo2eSelected: 'Total emissions from',
  upperTotalEmissionsKgCo2eSelected: 'Total emissions to',
  lowerScope1KgCo2eSelected: 'Scope 1 emissions from',
  upperScope1KgCo2eSelected: 'Scope 1 emissions to',
  lowerScope2KgCo2eSelected: 'Scope 2 emissions from',
  upperScope2KgCo2eSelected: 'Scope 2 emissions to',
  lowerScope3KgCo2eSelected: 'Scope 3 emissions from',
  upperScope3KgCo2eSelected: 'Scope 3 emissions to',
  lowerKgCo2ePerMillionRevenueSelected: 'Emissions per £m revenue from',
  upperKgCo2ePerMillionRevenueSelected: 'Emissions per £m revenue to',
  lowerKgCo2ePerEmployeeSelected: 'Emissions per employee from',
  upperKgCo2ePerEmployeeSelected: 'Emissions per employee to',
  hasLandTitle: 'Land title',
  landTenure: 'Land tenure',
  landCategory: 'Land category',
  accountantNameContains: 'Recorded accountant',
  selectedAccountants: 'Selected accountants',
  auditorNameContains: 'Recorded auditor',
  selectedAuditors: 'Selected auditors',
  lawyerNameContains: 'Recorded lawyer',
  supplierTagSelected: 'Likely supplier of tagged companies',
  customerTagSelected: 'Likely customer of tagged companies',
  associateWithOneSupplierOnly: 'One supplier only',
  webTrafficLow: 'Website visitors from',
  webTrafficHigh: 'Website visitors to',
  selectedOfficeCountries: 'Office countries',
  lowerGeographicalRevenueSelected: 'Overseas revenue from',
  upperGeographicalRevenueSelected: 'Overseas revenue to',
  selectedGeographicalRevenueTerritories: 'Overseas revenue territories',
  selectedGeographicalRevenueCountries: 'Overseas revenue countries',
  selectedImportExportCategories: 'Trade product categories',
  selectedImportingOrExportingOption: 'Import or export direction',
  importExportTimeScale: 'Trade activity within months',
  importExportFrequencyImportingOrExporting: 'Shipment direction',
  importExportFrequency: 'Minimum shipments',
  importExportFrequencyMonthCount: 'Shipment period (months)',
  lowerNumberOfKnownOfficeLocations: 'Office locations from',
  upperNumberOfKnownOfficeLocations: 'Office locations to',
  hasLEINumber: 'Has an LEI',
  selectedLEIStatus: 'LEI status',
  bankerNameContains: 'Recorded banker',
  selectedBankers: 'Selected bankers',
  noOutstandingCharges: 'No outstanding charges',
  chargeRegistered: 'Has a registered charge',
  outstandingChargeRegistered: 'Has an outstanding charge',
  chargeHolderNameContains: 'Charge holder name',
  chargeCreatedMonthsAgoLower: 'Charge created from months ago',
  chargeCreatedMonthsAgoUpper: 'Charge created to months ago',
  ignoreSatisfiedCharges: 'Ignore satisfied charges',
  amazonSeller: 'Amazon seller',
  listedOnGCloud: 'Listed on G-Cloud',
  hasContract: 'Government contract',
  lowerContractValueSelected: 'Contract value from',
  upperContractValueSelected: 'Contract value to',
  contractAwardedWithinMonths: 'Contract awarded within months',
  lowerContractStartMonthsSelected: 'Contract starts from months',
  upperContractStartMonthsSelected: 'Contract starts within months',
  lowerContractEndMonthsSelected: 'Contract ends from months',
  upperContractEndMonthsSelected: 'Contract ends within months',
  contractTitleContains: 'Contract title',
  contractDescriptionContains: 'Contract description',
  lowerNumberOfUBOs: 'Ultimate owners from',
  upperNumberOfUBOs: 'Ultimate owners to',
  selectedUBOCountries: 'Ultimate owner countries',
  lowerNumberOfPSCs: 'People with significant control from',
  upperNumberOfPSCs: 'People with significant control to',
  PSCsRealPersonsOnly: 'Individual PSCs only',
  nationalitiesOfPSCsSelected: 'PSC nationalities',
  lowerNumberOfDirectors: 'Directors from',
  upperNumberOfDirectors: 'Directors to',
  nationalitiesOfDirectorsSelected: 'Director nationalities',
  lowerAverageDirectorAgeRange: 'Average director age from',
  upperAverageDirectorAgeRange: 'Average director age to',
  hasShareFilingEvent: 'Issued or divided shares',
  shareFilingEventWithinDays: 'Share filing within days',
  lowerShareFilingCapitalValue: 'Share filing value from',
  upperShareFilingCapitalValue: 'Share filing value to',
  hasRaisedInvestment: 'Raised investment',
  lowerInvestmentAmount: 'Investment raised from',
  upperInvestmentAmount: 'Investment raised to',
  issuedAGrant: 'Grant awarded',
  lowerGrantAmount: 'Grant amount from',
  upperGrantAmount: 'Grant amount to',
  grantLowerMonthsUntilProjectEnd: 'Grant project ends from',
  grantUpperMonthsUntilProjectEnd: 'Grant project ends to',
  hasAnyPatentRecord: 'Patent record',
  hasActivePatent: 'Active patent',
  patentFiledWithinDays: 'Patent filed within days',
  lowerPatentAwardedDays: 'Patent awarded from days ago',
  upperPatentAwardedDays: 'Patent awarded to days ago',
  similarCompanyUrl: 'Similar company',
};

// These conventional filters have separate plan checks. PAF URL criteria are
// restored only after access is confirmed; the other gated URL criteria still
// open in Full, where their existing controls and permission checks apply.
export const FS_PAF_FILTER_KEYS = new Set([
  'hasLandTitle',
  'landTenure',
  'landCategory',
  ...rangeKeys(FS_EPC_SLIDERS),
]);

const FS_PERMISSION_GATED_URL_KEYS = new Set([
  'hasAnyPatentRecord',
  'hasActivePatent',
  'patentFiledWithinDays',
  'lowerPatentAwardedDays',
  'upperPatentAwardedDays',
  'noOutstandingCharges',
  'chargeRegistered',
  'outstandingChargeRegistered',
  'chargeHolderNameContains',
  'chargeCreatedMonthsAgoLower',
  'chargeCreatedMonthsAgoUpper',
  'ignoreSatisfiedCharges',
  ...FS_PAF_FILTER_KEYS,
]);

export interface FsFilterTag {
  param: string;
  value: unknown;
}

export function fsVisibleFilterKeys(play: FsPlayId): Set<string> {
  return new Set([...FS_COMMON_FILTER_KEYS, ...FS_PLAY_FILTER_KEYS[play]]);
}

/** The grouped workspace shows every section together. */
export function fsWorkspaceFilterKeys(): Set<string> {
  return new Set([
    ...FS_COMMON_FILTER_KEYS,
    ...Object.values(FS_PLAY_FILTER_KEYS).flat(),
  ]);
}

export function isActiveFsFilterTag(tag: FsFilterTag | null | undefined): boolean {
  if (!tag || !(tag.param in FS_FILTER_LABELS)) return false;
  if (Array.isArray(tag.value)) return tag.value.length > 0;
  if (typeof tag.value === 'string' && !tag.value.trim()) return false;
  return ![
    null, undefined, '', false, 'false', 'null', 'undefined', '[]', '{}',
  ].includes(tag.value as never);
}

/** A shared URL may only restore fields that the grouped workspace shows. */
export function unsupportedFsQueryKeys(
  params: Record<string, unknown>,
  _play: FsPlayId,
  hasPAFAccess = false
): string[] {
  const visible = fsWorkspaceFilterKeys();
  return Object.keys(params).filter(key =>
    key !== 'play' && key !== 'resultsMode' && key !== 'pageNumber' &&
    (!visible.has(key) ||
      (FS_PERMISSION_GATED_URL_KEYS.has(key) &&
        !(hasPAFAccess && FS_PAF_FILTER_KEYS.has(key))))
  );
}

/** The view a shared search belongs to: the one whose view-specific filters it uses, otherwise FX. */
export function inferFsPlay(params: Record<string, unknown>): FsPlayId | null {
  const matched = (Object.keys(FS_PLAY_FILTER_KEYS) as FsPlayId[])
    .filter(play => FS_PLAY_FILTER_KEYS[play].some(key => key in params));
  return matched[0] || 'fx_trade';
}
